import React, { useState, useCallback, useEffect } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { applyUpdate } from '../serviceWorkerRegistration';
import { api } from '../api';
import { TOKEN_KEY } from '../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWifi, faArrowDown, faDownload, faXmark } from '@fortawesome/free-solid-svg-icons';
import '../styles/PwaBanners.css';

interface PwaBannersProps {
  updateReady: boolean;
}

/**
 * PWA Banners:
 *   1. Offline banner — always shown when offline (fixed bottom)
 *   2. Update banner — shown when new SW is ready (fixed bottom)
 *   3. Install banner — shown when app can be installed (fixed bottom)
 *
 * All banners stack from bottom to top.
 */
export function PwaBanners({ updateReady }: PwaBannersProps) {
  const isOnline = useOnlineStatus();
  const { showInstallBanner, promptInstall, isIOS } = useInstallPrompt();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  // Standalone = installierte PWA (nicht im Browser-Tab)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;

  const handleUpdate = () => {
    applyUpdate();
    // Reload after short delay to let new SW take over
    setTimeout(() => window.location.reload(), 300);
  };

  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  // Load dismissed state from DB on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    api.get('/player/me/ui-states', token).then((data: any) => {
      const states = data?.states || {};
      const newDismissed: Record<string, boolean> = {};
      if (states['pwa_install_dismissed']) newDismissed.install = true;
      if (states['pwa_update_dismissed']) newDismissed.update = true;
      if (states['pwa_offline_dismissed']) newDismissed.offline = true;
      if (Object.keys(newDismissed).length > 0) setDismissed(newDismissed);
    }).catch(() => {});
  }, []);

  const dismiss = useCallback((key: string) => {
    setDismissed(prev => ({ ...prev, [key]: true }));
    // Save to DB if logged in
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      api.post('/player/me/ui-states', { state_key: `pwa_${key}_dismissed` }, token).catch(() => {});
    }
  }, []);

  return (
    <div className="pwa-banner-stack">
      {/* Install Banner — Mobile only (Android button + iOS manual instructions) */}
      {showInstallBanner && isMobile && !dismissed.install && (
        <div className="pwa-banner pwa-banner--install">
          <div className="pwa-banner-info">
            <span className="pwa-banner-icon">
              <FontAwesomeIcon icon={isIOS ? faArrowDown : faDownload} />
            </span>
            <span className="pwa-banner-text">
              {isIOS
                ? 'Tap "Add to Home Screen" in Safari to install'
                : 'Add NoWeakWeek to your home screen'}
            </span>
          </div>
          <div className="pwa-banner-actions">
            {!isIOS && (
              <button className="pwa-banner-btn pwa-banner-btn--install" onClick={promptInstall}>
                Install
              </button>
            )}
            <button className="pwa-banner-close" onClick={() => dismiss('install')} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      )}

      {/* Update Banner — Only shown in installed PWA (standalone mode) */}
      {updateReady && isStandalone && !dismissed.update && (
        <div className="pwa-banner pwa-banner--update">
          <div className="pwa-banner-info">
            <span className="pwa-banner-icon pwa-banner-icon--update">🔄</span>
            <span className="pwa-banner-text">New version available</span>
          </div>
          <div className="pwa-banner-actions">
            <button className="pwa-banner-btn pwa-banner-btn--update" onClick={handleUpdate}>
              Update
            </button>
            <button className="pwa-banner-close" onClick={() => dismiss('update')} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      )}

      {/* Offline Banner — No internet connection */}
      {!isOnline && !dismissed.offline && (
        <div className="pwa-banner pwa-banner--offline">
          <div className="pwa-banner-info">
            <span className="pwa-banner-icon">
              <FontAwesomeIcon icon={faWifi} />
            </span>
            <span className="pwa-banner-text">
              Offline — cached data may be outdated
            </span>
          </div>
          <button className="pwa-banner-close" onClick={() => dismiss('offline')} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
    </div>
  );
}

export default PwaBanners;
